import {useRequiredConfig} from '@domain/shifthandover'
import {filterSxStyles} from '@hconnect/common/styles'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {FilterList as FilterIcon} from '@mui/icons-material'
import {Badge, Button, Box, useMediaQuery, Theme} from '@mui/material'
import {sortingFilterParams} from '@widgets/shift-event-filters'
import {isEqual, omit} from 'lodash'
import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {ShiftSummaryFilter} from './ShiftSummaryFilter'
import type {ShiftSummaryFilterOptions} from './ShiftSummaryFilter'

type Props = {
  isCollapsible: boolean
  activeSettings: ShiftSummaryFilterOptions
  onChange: (next: ShiftSummaryFilterOptions) => void
  openMobileFilter: (state: boolean) => void
  isMobileFilterOpen: boolean
  plantId: string
}

export const ShiftSummaryFilterBar: React.FC<Props> = ({
  isCollapsible,
  activeSettings,
  onChange,
  openMobileFilter,
  isMobileFilterOpen,
  plantId
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const {shifts, kilnStoppageCodes, rawMillStoppageCodes, finishMillStoppageCodes} =
    useRequiredConfig(plantId)
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const [settings, setSettings] = useState<ShiftSummaryFilterOptions>(activeSettings)

  useEffect(() => {
    setSettings(activeSettings)
  }, [activeSettings])

  if (isCollapsible && !isMobileFilterOpen) {
    const filters = omit(settings, sortingFilterParams)
    const totalFilters: number = Object.keys(filters).reduce((acc, curr) => {
      if (Array.isArray(settings[curr])) {
        acc += settings[curr].length
      } else if (settings[curr]) {
        acc += 1
      }
      return acc
    }, 0)
    return (
      <Box data-test-id={'shift-summary-filter-bar'} sx={filterSxStyles.filterBar}>
        <Box sx={filterSxStyles.filterButton}>
          <Badge
            badgeContent={totalFilters}
            color="primary"
            data-test-id="shift-summary-filters-badge"
            overlap="circular"
          >
            <Button
              variant="text"
              startIcon={!isMobile && <FilterIcon />}
              sx={filterSxStyles.filterSearchButton}
              onClick={() => {
                openMobileFilter(true)
              }}
              data-test-id="shift-summary-filter-bar-toggle-button"
              id="shift-summary-filter-bar-toggle-button"
            >
              {isMobile ? <FilterIcon /> : t('shiftEvent.action.filter')}
            </Button>
          </Badge>
        </Box>
      </Box>
    )
  }

  const update = (next: ShiftSummaryFilterOptions) => {
    if (isEqual(settings, next)) {
      return
    }

    if (isCollapsible) {
      setSettings(next)
    } else {
      onChange(next)
    }
  }

  return (
    <Box
      data-test-id="shift-summary-filter-bar-box"
      sx={isMobileFilterOpen ? filterSxStyles.filterLayover : filterSxStyles.filterBar}
      display={'flex'}
      flexDirection={'column'}
    >
      {isCollapsible && (
        <Box sx={filterSxStyles.filterButton} mr={1} textAlign={'right'}>
          <Button
            data-test-id="shift-summary-filter-bar-cancel-button"
            id="shift-summary-filter-bar-cancel-button"
            variant={'contained'}
            onClick={() => {
              openMobileFilter(false)
              setSettings(activeSettings)
            }}
          >
            {t('action.cancel')}
          </Button>
        </Box>
      )}
      <ShiftSummaryFilter
        onChange={update}
        settings={settings}
        isCollapsible={isCollapsible}
        applyFilter={(filterSettings) => {
          openMobileFilter(false)
          onChange(filterSettings)
        }}
        plantId={plantId}
        shifts={shifts}
        timezone={timezone}
        plantNow={getPlantNow()}
        kilnStoppageCodes={kilnStoppageCodes}
        rawMillStoppageCodes={rawMillStoppageCodes}
        finishMillStoppageCodes={finishMillStoppageCodes}
      />
    </Box>
  )
}
